import { AppDefinition } from '../../types/apps';

export const TasksAppDefinition: AppDefinition = {
  appId: 'tasks',
  name: 'Tasks',
  version: '1.0.0',
  description: 'Simple and elegant task manager inspired by Things. Organize your tasks with projects, areas, and smart lists.',
  longDescription: `A beautifully designed task management app inspired by Things, built for River. TasksApp combines elegance with powerful features to help you organize your work and life.

**Key Features:**
- **Smart Lists**: Inbox, Today, Upcoming, Anytime, and Logbook views automatically organize your tasks
- **Projects & Areas**: Group related tasks into projects, and organize projects into life areas
- **Dual View Modes**: Switch between List view for detailed management and Board view for visual workflows
- **Drag & Drop**: Intuitive Kanban board with smooth drag-and-drop between status columns
- **Fast Shortcuts**: Linear-inspired single-key shortcuts (c, i, t, u, l, b) for blazing-fast navigation
- **Auto-save**: All changes save instantly as you type - no manual saving needed
- **Hidden Tasks**: Tasks are hidden from global entity lists, keeping your workspace clean

Perfect for individuals who want a simple, elegant task manager that integrates seamlessly with River's workspace.`,
  icon: {
    type: 'phosphor',
    phosphorIcon: 'CheckSquare',
    phosphorWeight: 'duotone',
    color: '#2563eb',
    metadata: {
      source: 'app',
      appId: 'tasks',
      isAppIcon: true
    }
  },
  
  // Creator information
  creator: {
    name: 'Brian Joseff',
    verified: true
  },
  
  // Store metadata
  categories: ['Productivity', 'Organization'],
  tags: ['tasks', 'todo', 'projects', 'kanban', 'productivity'],
  releaseDate: '2024-01-15T00:00:00Z',
  lastUpdated: new Date().toISOString(),
  events: {
    taskCreated: 'Task created',
    taskCompleted: 'Task completed',
    taskDeleted: 'Task deleted',
    projectCreated: 'Project created',
    areaCreated: 'Area created',
    taskMovedToToday: 'Task moved to Today',
    taskScheduled: 'Task scheduled'
  },
  entityTypes: [
    'Task',
    'Project',
    'Area'
  ],
  searchableSkeletonFields: {
    Task:    ['notes', 'tags'],
    Project: ['notes', 'tags'],
    Area:    ['notes', 'tags'],
  },
  component: 'TasksApp',
  requiresEntity: false,
  requiresProps: false,

  // Keyboard shortcuts — window-scoped, only active when TasksApp is focused.
  // Source of truth: gui/src/apps/TasksApp/shortcuts.ts
  hotkeys: {
    'c':       'createTask',
    'i':       'goToInbox',
    't':       'goToToday',
    'u':       'goToUpcoming',
    'a':       'goToAnytime',
    'l':       'goToLogbook',
    '/':       'goToSearch',
    'b':       'toggleDisplayMode',
    'cmd+b':   'toggleSidebar',
    '?':       'showShortcuts',
    'escape':  'close',
  },

  // Task management taxonomy
  taxonomy: {
    categories: [
      {
        id: 'views',
        name: 'Views',
        icon: '👁️',
        description: 'Different ways to view tasks',
        subcategories: ['inbox', 'today', 'upcoming', 'anytime', 'someday', 'logbook']
      },
      {
        id: 'organization',
        name: 'Organization',
        icon: '📁',
        description: 'Ways to organize tasks',
        subcategories: ['projects', 'areas', 'tags']
      }
    ],
    qualityIndicators: ['priority', 'completion', 'urgency'],
    pricingTypes: ['free'],
    compatibilityTags: ['productivity', 'organization', 'tasks']
  },

  // Command definitions for AI integration
  actions: {
    createTask: {
      name: 'Create Task',
      description: 'Create a new task',
      parameters: {
        title: { type: 'string', required: true },
        notes: { type: 'string', required: false },
        dueDate: { type: 'string', required: false },
        projectId: { type: 'string', required: false },
        areaId: { type: 'string', required: false },
        tags: { type: 'array', required: false },
        priority: { type: 'string', required: false, enum: ['low', 'medium', 'high'] }
      }
    },
    completeTask: {
      name: 'Complete Task',
      description: 'Mark a task as completed',
      parameters: {
        taskId: { type: 'string', required: true }
      }
    },
    createProject: {
      name: 'Create Project',
      description: 'Create a new project',
      parameters: {
        title: { type: 'string', required: true },
        notes: { type: 'string', required: false },
        areaId: { type: 'string', required: false },
        dueDate: { type: 'string', required: false }
      }
    },
    createArea: {
      name: 'Create Area',
      description: 'Create a new area',
      parameters: {
        title: { type: 'string', required: true },
        notes: { type: 'string', required: false }
      }
    },
    moveTaskToToday: {
      name: 'Move to Today',
      description: 'Move a task to Today view',
      parameters: {
        taskId: { type: 'string', required: true }
      }
    }
  },

  // Dynamic tool loading - these commands are auto-loaded in agentic mode
  agenticToolIds: [
    // Task operations
    'tasks_create_task',
    'tasks_update_task',
    'tasks_complete_task',
    'tasks_list_tasks',
    'tasks_move_to_today',
    
    // Project operations
    'tasks_create_project',
    'tasks_update_project',
    'tasks_list_projects',
    
    // Area operations
    'tasks_create_area',
    'tasks_list_areas'
  ]
};
